import { Injectable, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { flagMarkup } from './flags';
import { SITE } from './site-copy';

export interface LanguageChoice {
  code: string;
  short: string;
  native: string;
  english: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

export const LANGUAGES: LanguageChoice[] = [
  { code: 'en', short: 'EN', native: 'English', english: 'Global', flag: 'gb', dir: 'ltr' },
  { code: 'fr', short: 'FR', native: 'Français', english: 'French', flag: 'fr', dir: 'ltr' },
  { code: 'ar', short: 'AR', native: 'العربية', english: 'Arabic', flag: 'sa', dir: 'rtl' },
  { code: 'ms', short: 'MS', native: 'Bahasa Melayu', english: 'Malay', flag: 'my', dir: 'ltr' },
  { code: 'es', short: 'ES', native: 'Español', english: 'Spanish', flag: 'es', dir: 'ltr' },
  { code: 'de', short: 'DE', native: 'Deutsch', english: 'German', flag: 'de', dir: 'ltr' },
];

const TEXT: Record<string, Record<string, string>> = {
  en: {
    trade: 'Trade', resources: 'Resources', company: 'Company', ai: 'AI Trading', portfolio: 'Portfolio',
    signin: 'Sign in', openAccount: 'Open account', getInTouch: 'Get in Touch', symbol: 'Symbol',
    fourSteps: 'Four steps', marketList: 'Market list', support: 'Support', theAi: 'The AI', clients: 'Clients',
    platform: 'Platform', markets: 'Markets', bot: 'AI trading bot', fees: 'Fees and pricing', security: 'Security',
    learn: 'Learn', how: 'How it works', academy: 'Trading academy', blog: 'Blog', faq: 'FAQ', about: 'About us', contact: 'Contact',
    talkKicker: 'Reach us', talkTitle: 'Talk on Telegram.', talkBody: 'Message the support team on Telegram.',
    selectLanguage: 'Select other language', faqTitle: 'Frequently asked questions',
    faqIntro: 'Answers about opening an account, deposits, and what the prices on this site are.',
  },
  de: {
    trade: 'Handel', resources: 'Ressourcen', company: 'Unternehmen', ai: 'KI-Handel', portfolio: 'Portfolio',
    signin: 'Anmelden', openAccount: 'Konto eröffnen', getInTouch: 'Kontakt', symbol: 'Symbol',
    fourSteps: 'Vier Schritte', marketList: 'Marktliste', support: 'Support', theAi: 'Die KI', clients: 'Kunden',
    platform: 'Plattform', markets: 'Märkte', bot: 'KI-Handelsbot', fees: 'Gebühren', security: 'Sicherheit',
    learn: 'Lernen', how: 'So funktioniert es', academy: 'Handelsakademie', blog: 'Blog', faq: 'FAQ', about: 'Über uns', contact: 'Kontakt',
    talkKicker: 'Erreichen Sie uns', talkTitle: 'Schreiben Sie auf Telegram.', talkBody: 'Schreiben Sie dem Support auf Telegram.',
    selectLanguage: 'Andere Sprache wählen', faqTitle: 'Häufige Fragen',
    faqIntro: 'Antworten zum Konto, zu Einzahlungen und zu den Preisen auf dieser Seite.',
  },
  fr: {
    trade: 'Trader', resources: 'Ressources', company: 'Société', ai: 'Trading IA', portfolio: 'Portefeuille',
    signin: 'Connexion', openAccount: 'Ouvrir un compte', getInTouch: 'Contact', symbol: 'Symbole',
    fourSteps: 'Quatre étapes', marketList: 'Liste des marchés', support: 'Support', theAi: "L'IA", clients: 'Clients',
    platform: 'Plateforme', markets: 'Marchés', bot: 'Bot de trading IA', fees: 'Frais', security: 'Sécurité',
    learn: 'Apprendre', how: 'Comment ça marche', academy: 'Académie', blog: 'Blog', faq: 'FAQ', about: 'À propos', contact: 'Contact',
    talkKicker: 'Nous joindre', talkTitle: 'Écrire sur Telegram.', talkBody: 'Écrivez au support sur Telegram.',
    selectLanguage: 'Choisir une autre langue', faqTitle: 'Questions fréquentes',
    faqIntro: 'Réponses sur le compte, les dépôts, et les prix affichés sur ce site.',
  },
  ar: {
    trade: 'تداول', resources: 'مصادر', company: 'الشركة', ai: 'تداول بالذكاء', portfolio: 'المحفظة',
    signin: 'دخول', openAccount: 'افتح حسابًا', getInTouch: 'تواصل', symbol: 'رمز',
    fourSteps: 'أربع خطوات', marketList: 'قائمة الأسواق', support: 'الدعم', theAi: 'الذكاء', clients: 'العملاء',
    platform: 'المنصة', markets: 'الأسواق', bot: 'بوت التداول', fees: 'الرسوم', security: 'الأمان',
    learn: 'تعلّم', how: 'كيف يعمل', academy: 'الأكاديمية', blog: 'المدونة', faq: 'الأسئلة', about: 'من نحن', contact: 'اتصال',
    talkKicker: 'تواصل معنا', talkTitle: 'راسلنا على تيليغرام.', talkBody: 'راسل فريق الدعم على تيليغرام.',
    selectLanguage: 'اختر لغة أخرى', faqTitle: 'الأسئلة الشائعة',
    faqIntro: 'إجابات عن فتح الحساب والإيداع والأسعار المعروضة في هذا الموقع.',
  },
  it: {
    trade: 'Operatività', resources: 'Risorse', company: 'Società', ai: 'Trading IA', portfolio: 'Portafoglio',
    signin: 'Accedi', openAccount: 'Apri un conto', getInTouch: 'Contatto', symbol: 'Simbolo',
    fourSteps: 'Quattro passi', marketList: 'Lista mercati', support: 'Supporto', theAi: "L'IA", clients: 'Clienti',
    platform: 'Piattaforma', markets: 'Mercati', bot: 'Bot di trading IA', fees: 'Commissioni', security: 'Sicurezza',
    learn: 'Impara', how: 'Come funziona', academy: 'Accademia', blog: 'Blog', faq: 'FAQ', about: 'Chi siamo', contact: 'Contatto',
    talkKicker: 'Contattaci', talkTitle: 'Scrivi su Telegram.', talkBody: 'Scrivi al supporto su Telegram.',
    selectLanguage: 'Scegli un’altra lingua', faqTitle: 'Domande frequenti',
    faqIntro: 'Risposte su conto, depositi e prezzi mostrati su questo sito.',
  },
  es: {
    trade: 'Operar', resources: 'Recursos', company: 'Empresa', ai: 'Trading con IA', portfolio: 'Cartera',
    signin: 'Entrar', openAccount: 'Abrir cuenta', getInTouch: 'Contacto', symbol: 'Símbolo',
    fourSteps: 'Cuatro pasos', marketList: 'Lista de mercados', support: 'Soporte', theAi: 'La IA', clients: 'Clientes',
    platform: 'Plataforma', markets: 'Mercados', bot: 'Bot de trading IA', fees: 'Comisiones', security: 'Seguridad',
    learn: 'Aprender', how: 'Cómo funciona', academy: 'Academia', blog: 'Blog', faq: 'FAQ', about: 'Nosotros', contact: 'Contacto',
    talkKicker: 'Escríbenos', talkTitle: 'Habla por Telegram.', talkBody: 'Escribe al soporte por Telegram.',
    selectLanguage: 'Elegir otro idioma', faqTitle: 'Preguntas frecuentes',
    faqIntro: 'Respuestas sobre la cuenta, los depósitos y los precios de este sitio.',
  },
  pt: {
    trade: 'Operar', resources: 'Recursos', company: 'Empresa', ai: 'Trading com IA', portfolio: 'Carteira',
    signin: 'Entrar', openAccount: 'Abrir conta', getInTouch: 'Contacto', symbol: 'Símbolo',
    fourSteps: 'Quatro passos', marketList: 'Lista de mercados', support: 'Suporte', theAi: 'A IA', clients: 'Clientes',
    platform: 'Plataforma', markets: 'Mercados', bot: 'Bot de trading IA', fees: 'Taxas', security: 'Segurança',
    learn: 'Aprender', how: 'Como funciona', academy: 'Academia', blog: 'Blog', faq: 'FAQ', about: 'Sobre nós', contact: 'Contacto',
    talkKicker: 'Fale connosco', talkTitle: 'Fale no Telegram.', talkBody: 'Escreva ao suporte no Telegram.',
    selectLanguage: 'Escolher outro idioma', faqTitle: 'Perguntas frequentes',
    faqIntro: 'Respostas sobre a conta, os depósitos e os preços neste site.',
  },
  ms: {
    trade: 'Dagangan', resources: 'Sumber', company: 'Syarikat', ai: 'Dagangan AI', portfolio: 'Portfolio',
    signin: 'Log masuk', openAccount: 'Buka akaun', getInTouch: 'Hubungi', symbol: 'Simbol',
    fourSteps: 'Empat langkah', marketList: 'Senarai pasaran', support: 'Sokongan', theAi: 'AI', clients: 'Pelanggan',
    platform: 'Platform', markets: 'Pasaran', bot: 'Bot dagangan AI', fees: 'Yuran', security: 'Keselamatan',
    learn: 'Belajar', how: 'Cara ia berfungsi', academy: 'Akademi', blog: 'Blog', faq: 'FAQ', about: 'Tentang kami', contact: 'Hubungi',
    talkKicker: 'Hubungi kami', talkTitle: 'Mesej di Telegram.', talkBody: 'Tulis kepada sokongan di Telegram.',
    selectLanguage: 'Pilih bahasa lain', faqTitle: 'Soalan lazim',
    faqIntro: 'Jawapan tentang akaun, deposit, dan harga di laman ini.',
  },
  th: {
    trade: 'เทรด', resources: 'แหล่งข้อมูล', company: 'บริษัท', ai: 'เทรดด้วย AI', portfolio: 'พอร์ต',
    signin: 'เข้าสู่ระบบ', openAccount: 'เปิดบัญชี', getInTouch: 'ติดต่อ', symbol: 'สัญลักษณ์',
    fourSteps: 'สี่ขั้นตอน', marketList: 'รายการตลาด', support: 'ช่วยเหลือ', theAi: 'AI', clients: 'ลูกค้า',
    platform: 'แพลตฟอร์ม', markets: 'ตลาด', bot: 'บอทเทรด AI', fees: 'ค่าธรรมเนียม', security: 'ความปลอดภัย',
    learn: 'เรียนรู้', how: 'วิธีใช้งาน', academy: 'สถาบัน', blog: 'บล็อก', faq: 'คำถาม', about: 'เกี่ยวกับเรา', contact: 'ติดต่อ',
    talkKicker: 'ติดต่อเรา', talkTitle: 'ทักบน Telegram', talkBody: 'ส่งข้อความถึงฝ่ายช่วยเหลือบน Telegram',
    selectLanguage: 'เลือกภาษาอื่น', faqTitle: 'คำถามที่พบบ่อย',
    faqIntro: 'คำตอบเกี่ยวกับการเปิดบัญชี การฝาก และราคาบนเว็บไซต์นี้',
  },
  zh: {
    trade: '交易', resources: '资源', company: '公司', ai: 'AI 交易', portfolio: '投资组合',
    signin: '登录', openAccount: '开户', getInTouch: '联系', symbol: '代码',
    fourSteps: '四个步骤', marketList: '市场列表', support: '支持', theAi: 'AI', clients: '客户',
    platform: '平台', markets: '市场', bot: 'AI 交易机器人', fees: '费用', security: '安全',
    learn: '学习', how: '如何使用', academy: '学院', blog: '博客', faq: '常见问题', about: '关于我们', contact: '联系',
    talkKicker: '联系我们', talkTitle: '在 Telegram 上联系。', talkBody: '在 Telegram 上给支持团队发消息。',
    selectLanguage: '选择其他语言', faqTitle: '常见问题',
    faqIntro: '关于开户、入金，以及本站价格的说明。',
  },
  'zh-Hant': {
    trade: '交易', resources: '資源', company: '公司', ai: 'AI 交易', portfolio: '投資組合',
    signin: '登入', openAccount: '開戶', getInTouch: '聯絡', symbol: '代碼',
    fourSteps: '四個步驟', marketList: '市場列表', support: '支援', theAi: 'AI', clients: '客戶',
    platform: '平台', markets: '市場', bot: 'AI 交易機器人', fees: '費用', security: '安全',
    learn: '學習', how: '如何使用', academy: '學院', blog: '網誌', faq: '常見問題', about: '關於我們', contact: '聯絡',
    talkKicker: '聯絡我們', talkTitle: '在 Telegram 上聯絡。', talkBody: '在 Telegram 上傳訊給支援團隊。',
    selectLanguage: '選擇其他語言', faqTitle: '常見問題',
    faqIntro: '關於開戶、入金，以及本站價格的說明。',
  },
  id: {
    trade: 'Trading', resources: 'Sumber', company: 'Perusahaan', ai: 'Trading AI', portfolio: 'Portofolio',
    signin: 'Masuk', openAccount: 'Buka akun', getInTouch: 'Kontak', symbol: 'Simbol',
    fourSteps: 'Empat langkah', marketList: 'Daftar pasar', support: 'Dukungan', theAi: 'AI', clients: 'Klien',
    platform: 'Platform', markets: 'Pasar', bot: 'Bot trading AI', fees: 'Biaya', security: 'Keamanan',
    learn: 'Belajar', how: 'Cara kerja', academy: 'Akademi', blog: 'Blog', faq: 'FAQ', about: 'Tentang kami', contact: 'Kontak',
    talkKicker: 'Hubungi kami', talkTitle: 'Tulis di Telegram.', talkBody: 'Kirim pesan ke dukungan di Telegram.',
    selectLanguage: 'Pilih bahasa lain', faqTitle: 'Pertanyaan umum',
    faqIntro: 'Jawaban tentang akun, setoran, dan harga di situs ini.',
  },
  tl: {
    trade: 'Trade', resources: 'Sanggunian', company: 'Kumpanya', ai: 'AI trading', portfolio: 'Portfolio',
    signin: 'Mag-sign in', openAccount: 'Magbukas ng account', getInTouch: 'Makipag-ugnayan', symbol: 'Simbolo',
    fourSteps: 'Apat na hakbang', marketList: 'Listahan ng market', support: 'Suporta', theAi: 'Ang AI', clients: 'Mga kliyente',
    platform: 'Platform', markets: 'Mga market', bot: 'AI trading bot', fees: 'Mga bayarin', security: 'Seguridad',
    learn: 'Matuto', how: 'Paano gumagana', academy: 'Akademya', blog: 'Blog', faq: 'FAQ', about: 'Tungkol sa amin', contact: 'Contact',
    talkKicker: 'Kontakin kami', talkTitle: 'Magsulat sa Telegram.', talkBody: 'Magmensahe sa suporta sa Telegram.',
    selectLanguage: 'Pumili ng ibang wika', faqTitle: 'Mga madalas na tanong',
    faqIntro: 'Mga sagot tungkol sa account, deposito, at mga presyo sa site na ito.',
  },
  vi: {
    trade: 'Giao dịch', resources: 'Tài nguyên', company: 'Công ty', ai: 'Giao dịch AI', portfolio: 'Danh mục',
    signin: 'Đăng nhập', openAccount: 'Mở tài khoản', getInTouch: 'Liên hệ', symbol: 'Mã',
    fourSteps: 'Bốn bước', marketList: 'Danh sách thị trường', support: 'Hỗ trợ', theAi: 'AI', clients: 'Khách hàng',
    platform: 'Nền tảng', markets: 'Thị trường', bot: 'Bot giao dịch AI', fees: 'Phí', security: 'Bảo mật',
    learn: 'Học', how: 'Cách hoạt động', academy: 'Học viện', blog: 'Blog', faq: 'Hỏi đáp', about: 'Về chúng tôi', contact: 'Liên hệ',
    talkKicker: 'Liên hệ', talkTitle: 'Nhắn trên Telegram.', talkBody: 'Nhắn cho hỗ trợ trên Telegram.',
    selectLanguage: 'Chọn ngôn ngữ khác', faqTitle: 'Câu hỏi thường gặp',
    faqIntro: 'Câu trả lời về mở tài khoản, nạp tiền, và giá trên trang này.',
  },
};

const FAQ: Record<string, { q: string; a: string }[]> = {
  en: [
    { q: 'How do I open an account?', a: 'Open the sign-up page and enter your name, email, phone, and password. Opening an account has no fee.' },
    { q: 'What does a new account start with?', a: 'A new account starts at $0.' },
    { q: 'How do I sign in?', a: 'Use the email and password from sign-up on the sign-in page.' },
    { q: 'I forgot my password. What do I do?', a: 'Message support on Telegram. The site does not email a reset link.' },
    { q: 'What account types are there?', a: 'Classic from $250, Silver from $2,500, Gold from $10,000, Platinum from $50,000, and VIP from $100,000.' },
    { q: 'How do I make a deposit?', a: 'Sign in, open Deposit, choose the account type, then choose Bitcoin, Ethereum, Solana, or Tron. Send only that coin to the address shown, then tell support on Telegram.' },
    { q: 'How do I reach support?', a: 'Use Telegram, or email support@capitrade.net.' },
  ],
};

function fillFaq(items: { q: string; a: string }[]) {
  return items;
}

FAQ['de'] = fillFaq([
  { q: 'Wie eröffne ich ein Konto?', a: 'Öffnen Sie die Anmeldeseite und geben Sie Name, E-Mail, Telefon und Passwort ein. Die Eröffnung ist kostenlos.' },
  { q: 'Womit startet ein neues Konto?', a: 'Ein neues Konto startet bei 0 $.' },
  { q: 'Wie melde ich mich an?', a: 'Nutzen Sie E-Mail und Passwort der Anmeldung auf der Anmeldeseite.' },
  { q: 'Ich habe mein Passwort vergessen.', a: 'Schreiben Sie dem Support auf Telegram. Die Seite sendet keinen Link zum Zurücksetzen.' },
  { q: 'Welche Kontotypen gibt es?', a: 'Classic ab 250 $, Silver ab 2.500 $, Gold ab 10.000 $, Platinum ab 50.000 $ und VIP ab 100.000 $.' },
  { q: 'Wie zahle ich ein?', a: 'Melden Sie sich an, öffnen Sie Deposit, wählen Sie den Kontotyp und dann Bitcoin, Ethereum, Solana oder Tron. Senden Sie nur diese Münze an die angezeigte Adresse und informieren Sie den Support auf Telegram.' },
  { q: 'Sind die Preise live?', a: 'Nein. Die Preise auf der Seite sind simuliert. Es wird nichts für Sie gekauft oder verkauft.' },
  { q: 'Wie erreiche ich den Support?', a: 'Nutzen Sie Telegram oder schreiben Sie an support@capitrade.net.' },
]);
FAQ['fr'] = fillFaq([
  { q: 'Comment ouvrir un compte ?', a: 'Ouvrez la page d’inscription et entrez votre nom, votre e-mail, votre téléphone et un mot de passe. L’ouverture est gratuite.' },
  { q: 'Avec quoi un nouveau compte commence-t-il ?', a: 'Un nouveau compte commence à 0 $.' },
  { q: 'Comment me connecter ?', a: 'Utilisez l’e-mail et le mot de passe de l’inscription sur la page de connexion.' },
  { q: 'J’ai oublié mon mot de passe.', a: 'Écrivez au support sur Telegram. Le site n’envoie pas de lien de réinitialisation.' },
  { q: 'Quels types de compte existent ?', a: 'Classic dès 250 $, Silver dès 2 500 $, Gold dès 10 000 $, Platinum dès 50 000 $ et VIP dès 100 000 $.' },
  { q: 'Comment faire un dépôt ?', a: 'Connectez-vous, ouvrez Dépôt, choisissez le type de compte, puis Bitcoin, Ethereum, Solana ou Tron. Envoyez seulement cette pièce à l’adresse affichée, puis prévenez le support sur Telegram.' },
  { q: 'Les prix sont-ils en direct ?', a: 'Non. Les prix du site sont simulés. Rien n’est acheté ou vendu pour vous.' },
  { q: 'Comment joindre le support ?', a: 'Utilisez Telegram, ou écrivez à support@capitrade.net.' },
]);
FAQ['ar'] = fillFaq([
  { q: 'كيف أفتح حسابًا؟', a: 'افتح صفحة التسجيل وأدخل الاسم والبريد والهاتف وكلمة المرور. فتح الحساب بلا رسوم.' },
  { q: 'بماذا يبدأ الحساب الجديد؟', a: 'الحساب الجديد يبدأ من 0 دولار.' },
  { q: 'كيف أسجّل الدخول؟', a: 'استخدم البريد وكلمة المرور من التسجيل في صفحة الدخول.' },
  { q: 'نسيت كلمة المرور.', a: 'راسل الدعم على تيليغرام. الموقع لا يرسل رابط إعادة تعيين.' },
  { q: 'ما أنواع الحسابات؟', a: 'Classic من 250 دولارًا، Silver من 2,500، Gold من 10,000، Platinum من 50,000، وVIP من 100,000.' },
  { q: 'كيف أودع؟', a: 'سجّل الدخول، افتح الإيداع، اختر نوع الحساب ثم بيتكوين أو إيثيريوم أو سولانا أو ترون. أرسل هذه العملة فقط إلى العنوان الظاهر، ثم أخبر الدعم على تيليغرام.' },
  { q: 'هل الأسعار مباشرة؟', a: 'لا. الأسعار على الموقع محاكاة. لا يُشترى شيء ولا يُباع نيابة عنك.' },
  { q: 'كيف أصل إلى الدعم؟', a: 'استخدم تيليغرام، أو راسل support@capitrade.net.' },
]);
FAQ['it'] = fillFaq([
  { q: 'Come apro un conto?', a: 'Apri la pagina di iscrizione e inserisci nome, email, telefono e password. L’apertura non ha un costo.' },
  { q: 'Da quanto parte un conto nuovo?', a: 'Un conto nuovo parte da 0 $.' },
  { q: 'Come entro?', a: 'Usa email e password dell’iscrizione nella pagina di accesso.' },
  { q: 'Ho dimenticato la password.', a: 'Scrivi al supporto su Telegram. Il sito non invia un link di reset.' },
  { q: 'Quali tipi di conto ci sono?', a: 'Classic da 250 $, Silver da 2.500 $, Gold da 10.000 $, Platinum da 50.000 $ e VIP da 100.000 $.' },
  { q: 'Come deposito?', a: 'Accedi, apri Deposito, scegli il tipo di conto e poi Bitcoin, Ethereum, Solana o Tron. Invia solo quella moneta all’indirizzo mostrato, poi avvisa il supporto su Telegram.' },
  { q: 'I prezzi sono dal vivo?', a: 'No. I prezzi sul sito sono simulati. Non viene comprato né venduto nulla per te.' },
  { q: 'Come contatto il supporto?', a: 'Usa Telegram, oppure scrivi a support@capitrade.net.' },
]);
FAQ['es'] = fillFaq([
  { q: '¿Cómo abro una cuenta?', a: 'Abre la página de registro e introduce nombre, correo, teléfono y contraseña. Abrir la cuenta no tiene coste.' },
  { q: '¿Con qué empieza una cuenta nueva?', a: 'Una cuenta nueva empieza en 0 $.' },
  { q: '¿Cómo entro?', a: 'Usa el correo y la contraseña del registro en la página de acceso.' },
  { q: 'Olvidé mi contraseña.', a: 'Escribe al soporte por Telegram. El sitio no envía un enlace de restablecimiento.' },
  { q: '¿Qué tipos de cuenta hay?', a: 'Classic desde 250 $, Silver desde 2.500 $, Gold desde 10.000 $, Platinum desde 50.000 $ y VIP desde 100.000 $.' },
  { q: '¿Cómo hago un depósito?', a: 'Entra, abre Depósito, elige el tipo de cuenta y luego Bitcoin, Ethereum, Solana o Tron. Envía solo esa moneda a la dirección mostrada y avisa al soporte por Telegram.' },
  { q: '¿Los precios son en vivo?', a: 'No. Los precios del sitio son simulados. No se compra ni se vende nada por ti.' },
  { q: '¿Cómo contacto con soporte?', a: 'Usa Telegram o escribe a support@capitrade.net.' },
]);
FAQ['pt'] = fillFaq([
  { q: 'Como abro uma conta?', a: 'Abra a página de registo e introduza nome, e-mail, telefone e palavra-passe. Abrir a conta não tem taxa.' },
  { q: 'Com quanto começa uma conta nova?', a: 'Uma conta nova começa em 0 $.' },
  { q: 'Como entro?', a: 'Use o e-mail e a palavra-passe do registo na página de entrada.' },
  { q: 'Esqueci a palavra-passe.', a: 'Escreva ao suporte no Telegram. O site não envia um link de reposição.' },
  { q: 'Que tipos de conta existem?', a: 'Classic a partir de 250 $, Silver a partir de 2.500 $, Gold a partir de 10.000 $, Platinum a partir de 50.000 $ e VIP a partir de 100.000 $.' },
  { q: 'Como faço um depósito?', a: 'Entre, abra Depósito, escolha o tipo de conta e depois Bitcoin, Ethereum, Solana ou Tron. Envie só essa moeda para o endereço mostrado e avise o suporte no Telegram.' },
  { q: 'Os preços são ao vivo?', a: 'Não. Os preços no site são simulados. Nada é comprado ou vendido por si.' },
  { q: 'Como falo com o suporte?', a: 'Use o Telegram ou escreva para support@capitrade.net.' },
]);
FAQ['ms'] = fillFaq([
  { q: 'Bagaimana saya membuka akaun?', a: 'Buka halaman daftar dan isi nama, e-mel, telefon, serta kata laluan. Membuka akaun tiada yuran.' },
  { q: 'Akaun baharu bermula dengan apa?', a: 'Akaun baharu bermula pada $0.' },
  { q: 'Bagaimana saya log masuk?', a: 'Guna e-mel dan kata laluan pendaftaran di halaman log masuk.' },
  { q: 'Saya terlupa kata laluan.', a: 'Mesej sokongan di Telegram. Laman ini tidak menghantar pautan set semula.' },
  { q: 'Apakah jenis akaun?', a: 'Classic dari $250, Silver dari $2,500, Gold dari $10,000, Platinum dari $50,000, dan VIP dari $100,000.' },
  { q: 'Bagaimana saya membuat deposit?', a: 'Log masuk, buka Deposit, pilih jenis akaun, kemudian Bitcoin, Ethereum, Solana, atau Tron. Hantar syiling itu sahaja ke alamat yang ditunjukkan, kemudian beritahu sokongan di Telegram.' },
  { q: 'Adakah harga ini langsung?', a: 'Tidak. Harga di laman ini disimulasikan. Tiada apa-apa dibeli atau dijual untuk anda.' },
  { q: 'Bagaimana saya menghubungi sokongan?', a: 'Guna Telegram, atau e-mel support@capitrade.net.' },
]);
FAQ['th'] = fillFaq([
  { q: 'เปิดบัญชีอย่างไร?', a: 'เปิดหน้าสมัคร แล้วกรอกชื่อ อีเมล โทรศัพท์ และรหัสผ่าน การเปิดบัญชีไม่มีค่าธรรมเนียม' },
  { q: 'บัญชีใหม่เริ่มที่เท่าไร?', a: 'บัญชีใหม่เริ่มที่ $0' },
  { q: 'เข้าสู่ระบบอย่างไร?', a: 'ใช้อีเมลและรหัสผ่านจากตอนสมัครในหน้าเข้าสู่ระบบ' },
  { q: 'ลืมรหัสผ่าน', a: 'ส่งข้อความถึงฝ่ายช่วยเหลือบน Telegram เว็บไซต์ไม่ส่งลิงก์รีเซ็ต' },
  { q: 'มีบัญชีแบบใดบ้าง?', a: 'Classic ตั้งแต่ $250, Silver ตั้งแต่ $2,500, Gold ตั้งแต่ $10,000, Platinum ตั้งแต่ $50,000 และ VIP ตั้งแต่ $100,000' },
  { q: 'ฝากเงินอย่างไร?', a: 'เข้าสู่ระบบ เปิด Deposit เลือกประเภทบัญชี แล้วเลือก Bitcoin, Ethereum, Solana หรือ Tron ส่งเฉพาะเหรียญนั้นไปยังที่อยู่ที่แสดง แล้วแจ้งฝ่ายช่วยเหลือบน Telegram' },
  { q: 'ราคาเป็นราคาจริงหรือไม่?', a: 'ไม่ใช่ ราคาบนเว็บไซต์เป็นการจำลอง ไม่มีการซื้อหรือขายแทนคุณ' },
  { q: 'ติดต่อฝ่ายช่วยเหลืออย่างไร?', a: 'ใช้ Telegram หรืออีเมล support@capitrade.net' },
]);
FAQ['zh'] = fillFaq([
  { q: '如何开户？', a: '打开注册页，填写姓名、邮箱、电话和密码。开户不收费。' },
  { q: '新账户从多少开始？', a: '新账户从 0 美元开始。' },
  { q: '如何登录？', a: '在登录页使用注册时的邮箱和密码。' },
  { q: '我忘记了密码。', a: '在 Telegram 上联系支持。本站不会发送重置链接。' },
  { q: '有哪些账户类型？', a: 'Classic 从 250 美元起，Silver 从 2,500 美元起，Gold 从 10,000 美元起，Platinum 从 50,000 美元起，VIP 从 100,000 美元起。' },
  { q: '如何入金？', a: '登录后打开 Deposit，选择账户类型，再选择比特币、以太坊、Solana 或波场。只把该币发到显示的地址，然后在 Telegram 上告诉支持。' },
  { q: '价格是实时的吗？', a: '不是。本站价格是模拟的。不会替你买入或卖出。' },
  { q: '如何联系支持？', a: '使用 Telegram，或发送邮件到 support@capitrade.net。' },
]);
FAQ['zh-Hant'] = fillFaq([
  { q: '如何開戶？', a: '打開註冊頁，填寫姓名、電郵、電話和密碼。開戶不收費。' },
  { q: '新帳戶從多少開始？', a: '新帳戶從 0 美元開始。' },
  { q: '如何登入？', a: '在登入頁使用註冊時的電郵和密碼。' },
  { q: '我忘記了密碼。', a: '在 Telegram 上聯絡支援。本站不會寄出重設連結。' },
  { q: '有哪些帳戶類型？', a: 'Classic 從 250 美元起，Silver 從 2,500 美元起，Gold 從 10,000 美元起，Platinum 從 50,000 美元起，VIP 從 100,000 美元起。' },
  { q: '如何入金？', a: '登入後打開 Deposit，選擇帳戶類型，再選擇比特幣、以太坊、Solana 或波場。只把該幣發到顯示的地址，然後在 Telegram 上告訴支援。' },
  { q: '價格是即時的嗎？', a: '不是。本站價格是模擬的。不會替你買入或賣出。' },
  { q: '如何聯絡支援？', a: '使用 Telegram，或寄信到 support@capitrade.net。' },
]);
FAQ['id'] = fillFaq([
  { q: 'Bagaimana saya membuka akun?', a: 'Buka halaman daftar dan isi nama, email, telepon, serta kata sandi. Membuka akun tidak dipungut biaya.' },
  { q: 'Akun baru mulai dari berapa?', a: 'Akun baru mulai dari $0.' },
  { q: 'Bagaimana saya masuk?', a: 'Gunakan email dan kata sandi pendaftaran di halaman masuk.' },
  { q: 'Saya lupa kata sandi.', a: 'Kirim pesan ke dukungan di Telegram. Situs ini tidak mengirim tautan reset.' },
  { q: 'Jenis akun apa yang ada?', a: 'Classic dari $250, Silver dari $2.500, Gold dari $10.000, Platinum dari $50.000, dan VIP dari $100.000.' },
  { q: 'Bagaimana saya menyetor?', a: 'Masuk, buka Deposit, pilih jenis akun, lalu Bitcoin, Ethereum, Solana, atau Tron. Kirim hanya koin itu ke alamat yang ditampilkan, lalu beri tahu dukungan di Telegram.' },
  { q: 'Apakah harganya langsung?', a: 'Tidak. Harga di situs ini disimulasikan. Tidak ada yang dibeli atau dijual untuk Anda.' },
  { q: 'Bagaimana saya menghubungi dukungan?', a: 'Gunakan Telegram, atau email support@capitrade.net.' },
]);
FAQ['tl'] = fillFaq([
  { q: 'Paano ako magbubukas ng account?', a: 'Buksan ang sign-up page at ilagay ang pangalan, email, telepono, at password. Libre ang pagbukas ng account.' },
  { q: 'Sa magkano nagsisimula ang bagong account?', a: 'Nagsisimula ang bagong account sa $0.' },
  { q: 'Paano ako mag-sign in?', a: 'Gamitin ang email at password mula sa sign-up sa sign-in page.' },
  { q: 'Nakalimutan ko ang password.', a: 'Magmensahe sa suporta sa Telegram. Hindi nagpapadala ng reset link ang site.' },
  { q: 'Anong mga uri ng account ang meron?', a: 'Classic mula $250, Silver mula $2,500, Gold mula $10,000, Platinum mula $50,000, at VIP mula $100,000.' },
  { q: 'Paano ako magdeposito?', a: 'Mag-sign in, buksan ang Deposit, piliin ang uri ng account, tapos Bitcoin, Ethereum, Solana, o Tron. Ipadala lang ang coin na iyon sa address na ipinapakita, tapos sabihin sa suporta sa Telegram.' },
  { q: 'Live ba ang mga presyo?', a: 'Hindi. Simulated ang mga presyo sa site. Walang binibili o ibinebenta para sa iyo.' },
  { q: 'Paano ko makokontak ang suporta?', a: 'Gamitin ang Telegram, o mag-email sa support@capitrade.net.' },
]);
FAQ['vi'] = fillFaq([
  { q: 'Làm sao để mở tài khoản?', a: 'Mở trang đăng ký và nhập tên, email, điện thoại và mật khẩu. Mở tài khoản không mất phí.' },
  { q: 'Tài khoản mới bắt đầu từ bao nhiêu?', a: 'Tài khoản mới bắt đầu từ 0 $.' },
  { q: 'Làm sao để đăng nhập?', a: 'Dùng email và mật khẩu lúc đăng ký trên trang đăng nhập.' },
  { q: 'Tôi quên mật khẩu.', a: 'Nhắn hỗ trợ trên Telegram. Trang không gửi liên kết đặt lại.' },
  { q: 'Có những loại tài khoản nào?', a: 'Classic từ 250 $, Silver từ 2.500 $, Gold từ 10.000 $, Platinum từ 50.000 $ và VIP từ 100.000 $.' },
  { q: 'Làm sao để nạp tiền?', a: 'Đăng nhập, mở Deposit, chọn loại tài khoản, rồi Bitcoin, Ethereum, Solana hoặc Tron. Chỉ gửi đồng đó tới địa chỉ được hiện, sau đó báo hỗ trợ trên Telegram.' },
  { q: 'Giá có phải giá thật không?', a: 'Không. Giá trên trang là mô phỏng. Không có gì được mua hoặc bán hộ bạn.' },
  { q: 'Làm sao liên hệ hỗ trợ?', a: 'Dùng Telegram, hoặc email support@capitrade.net.' },
]);

const KEY = 'capitrade.lang';


@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly flagCache = new Map<string, SafeUrl>();
  readonly code = signal(this.read());
  readonly current = computed(() => LANGUAGES.find((item) => item.code === this.code()) ?? LANGUAGES[0]);
  readonly faqs = computed(() => FAQ[this.code()] ?? FAQ['en']);

  flagSrc(id: string): SafeUrl {
    const key = id || 'gb';
    const cached = this.flagCache.get(key);
    if (cached) {
      return cached;
    }
    const url = this.sanitizer.bypassSecurityTrustUrl(
      `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(flagMarkup(key))}`,
    );
    this.flagCache.set(key, url);
    return url;
  }

  constructor() {
    this.apply(this.code());
  }

  t(key: string): string {
    const code = this.code();
    return TEXT[code]?.[key] ?? SITE[code]?.[key] ?? TEXT['en']?.[key] ?? SITE['en']?.[key] ?? key;
  }

  choose(code: string): void {
    if (!LANGUAGES.some((item) => item.code === code)) {
      return;
    }
    localStorage.setItem(KEY, code);
    this.code.set(code);
    this.apply(code);
  }

  private read(): string {
    const saved = localStorage.getItem(KEY) ?? 'en';
    return LANGUAGES.some((item) => item.code === saved) ? saved : 'en';
  }

  private apply(code: string): void {
    const choice = LANGUAGES.find((item) => item.code === code) ?? LANGUAGES[0];
    document.documentElement.lang = choice.code;
    document.documentElement.dir = choice.dir;
    document.title = SITE[choice.code]?.['docTitle'] ?? SITE['en']['docTitle'];
  }
}
